import { create } from 'zustand';
import { apiClient } from '../lib';

// Dev mode: bypass auth when no Privy configured or mock API enabled
const DEV_MODE = import.meta.env.VITE_MOCK_API === 'true' ||
  (import.meta.env.DEV && !import.meta.env.VITE_PRIVY_APP_ID);

const DEV_USER = {
  id: 'dev-user-1',
  email: 'dev@ponzinomics.local',
  walletAddress: '0x1234...5678',
  displayName: 'Dev User',
};

interface User {
  id: string;
  email?: string;
  walletAddress?: string;
  displayName?: string;
  avatar?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  devMode: boolean;
}

interface AuthActions {
  loginWithPrivy: (privyToken: string) => Promise<void>;
  loginDev: () => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>((set) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  devMode: DEV_MODE,

  // Actions
  loginWithPrivy: async (privyToken: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.loginWithPrivy(privyToken);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Login failed',
        isLoading: false,
      });
      throw err;
    }
  },

  loginDev: () => {
    if (!DEV_MODE) return;
    set({
      user: DEV_USER,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  },

  logout: () => {
    apiClient.logout();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  checkAuth: async () => {
    // In dev mode, don't check auth via API
    if (DEV_MODE) {
      set({ isLoading: false });
      return;
    }

    if (!apiClient.isAuthenticated()) {
      set({ isLoading: false });
      return;
    }

    set({ isLoading: true });
    try {
      const user = await apiClient.getMe();
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      apiClient.logout();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
